import {
  AdminApiError,
  type AdminClient,
  type GreedykingWeightsOverride,
  type SimulateResponse,
  type VerifyResponse,
} from '@apx/admin-sdk';
import { prettyGame } from '../games.js';
import { greedykingWeightsSchema } from '@apx/integrator-config/schemas';

// Greedyking-only page. Sicbo → sicbo-weights.ts, dragontiger →
// dragontiger-weights.ts (dispatched by gameId in game-detail.ts).
type WeightsOverride = GreedykingWeightsOverride;
const weightsSchema = greedykingWeightsSchema;
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';

export interface WeightsPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  roundEngineBaseUrl: string;
}

const DEFAULT_WEIGHTS: WeightsOverride = {
  version: 3,
  primary: {
    carrots: 0.15833,
    pakcoi: 0.15833,
    corn: 0.15833,
    tomato: 0.15833,
    hotdog: 0.07308,
    bbq: 0.04872,
    meat: 0.02923,
    steak: 0.01624,
    house: 0.19941,
  },
  special: {
    none: 0.987,
    pizza: 0.001,
    salad: 0.012,
  },
  targetRtp: 0.98,
};

const PRIMARY_SLOTS = Object.keys(DEFAULT_WEIGHTS.primary);
const SPECIAL_SLOTS = Object.keys(DEFAULT_WEIGHTS.special);

/**
 * Quick-pick presets for the special-tier draw. Each row sets the
 * `special` weights only — primary stays untouched. The operator still
 * needs to hit "Propose change" to send it through the two-admin
 * approval flow.
 *
 *   default  → matches DEFAULT_WEIGHTS shipped from the game module
 *   common   → ~2× more frequent specials, still inside the RTP band
 *   demo     → very frequent specials for QA / showcase only. RTP runs
 *              hot; do NOT leave this on in production.
 */
interface SpecialPreset {
  id: string;
  label: string;
  description: string;
  special: WeightsOverride['special'];
}
const SPECIAL_PRESETS: ReadonlyArray<SpecialPreset> = [
  {
    id: 'default',
    label: 'Default (shipped)',
    description: 'salad ≈ 1 / 83 rounds · pizza ≈ 1 / 1000 rounds',
    special: { none: 0.987, pizza: 0.001, salad: 0.012 },
  },
  {
    id: 'common',
    label: 'Common',
    description: 'salad ≈ 1 / 40 rounds · pizza ≈ 1 / 500 rounds',
    special: { none: 0.973, pizza: 0.002, salad: 0.025 },
  },
  {
    id: 'demo',
    label: 'Demo / QA only',
    description: 'salad ≈ 1 / 20 rounds · pizza ≈ 1 / 200 rounds — RTP runs hot',
    special: { none: 0.94, pizza: 0.005, salad: 0.055 },
  },
];

export async function renderWeightsPage(root: HTMLElement, deps: WeightsPageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">RNG &amp; RTP — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Two-tier provably-fair distribution: PRIMARY draw picks the food cabin (or house), SPECIAL draw fires pizza/salad bonus independently. Changes go through two-admin approval. Dry-run simulator + audit verifier below.</p>

    <div class="card">
      <h2>Primary tier — cabin draw (sum must be 1.0)</h2>
      <div class="form-grid">${PRIMARY_SLOTS.map(
        (s) => `<label>${s}
          <input data-primary="${s}" type="number" min="0" max="1" step="0.0001" />
        </label>`,
      ).join('')}</div>
      <p class="muted" style="margin-top:8px"><span id="primary-sum">sum: 0</span></p>
    </div>

    <div class="card">
      <h2>Special tier — bonus draw (sum must be 1.0)</h2>
      <p class="muted" style="margin-top:0">Pizza pays all meat picks · Salad pays all veg picks · None = no special this round. Keep pizza + salad small or RTP blows up.</p>
      <div class="row" id="special-presets" style="margin:0 0 10px;flex-wrap:wrap;gap:6px">
        <span class="muted" style="align-self:center;font-size:12px">Quick preset:</span>
        ${SPECIAL_PRESETS.map(
          (p) => `<button type="button" class="ghost" data-preset="${p.id}" title="${p.description}">${p.label}</button>`,
        ).join('')}
      </div>
      <div class="form-grid">${SPECIAL_SLOTS.map(
        (s) => `<label>${s}
          <input data-special="${s}" type="number" min="0" max="1" step="0.0001" />
        </label>`,
      ).join('')}</div>
      <p class="muted" style="margin-top:8px"><span id="special-sum">sum: 0</span></p>
    </div>

    <div class="card">
      <div class="row">
        <label style="flex:1">Target RTP (0–1)
          <input id="target-rtp" type="number" min="0" max="1" step="0.001" />
        </label>
        <span id="weights-rtp" class="muted">target RTP: —</span>
      </div>
      <div class="row">
        <button id="weights-normalize" class="ghost">Auto-normalize both tables</button>
        <button id="weights-save" class="primary">Propose change</button>
        <button id="weights-reset" class="ghost">Reset to defaults</button>
        <span id="weights-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Dry-run simulator</h2>
      <div class="row">
        <label>Rounds <input id="sim-rounds" type="number" min="100" max="100000" step="500" value="10000" /></label>
        <label>Bet per slot <input id="sim-bet" type="number" min="1" max="1000000" step="100" value="1000" /></label>
        <button id="sim-run">Run simulation</button>
        <span id="sim-msg" class="muted"></span>
      </div>
      <div id="sim-result"></div>
    </div>

    <div class="card">
      <h2>Audit verifier</h2>
      <p class="muted">Re-derive any finished round from its public seeds. No admin auth required — provably-fair is meaningless if gated.</p>
      <div class="row">
        <label style="flex:1">Round ID
          <input id="verify-id" type="text" placeholder="01HABC…" />
        </label>
        <button id="verify-run">Verify</button>
      </div>
      <div id="verify-result"></div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="weights-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  // Clamp a distribution input to [0,1] on blur/commit so a typed >1 or negative
  // snaps back before it can be proposed (mirrors the schema's per-weight bound).
  function clampUnit(el: HTMLInputElement): void {
    const n = Number(el.value);
    if (Number.isFinite(n)) el.value = String(Math.min(1, Math.max(0, n)));
  }
  const primaryInputs = new Map<string, HTMLInputElement>();
  for (const s of PRIMARY_SLOTS) {
    const el = root.querySelector<HTMLInputElement>(`input[data-primary="${s}"]`);
    if (el) {
      primaryInputs.set(s, el);
      el.addEventListener('input', updateSums);
      el.addEventListener('change', () => {
        clampUnit(el);
        updateSums();
      });
    }
  }
  const specialInputs = new Map<string, HTMLInputElement>();
  for (const s of SPECIAL_SLOTS) {
    const el = root.querySelector<HTMLInputElement>(`input[data-special="${s}"]`);
    if (el) {
      specialInputs.set(s, el);
      el.addEventListener('input', updateSums);
      el.addEventListener('change', () => {
        clampUnit(el);
        updateSums();
      });
    }
  }
  const targetRtpEl = root.querySelector<HTMLInputElement>('#target-rtp')!;
  targetRtpEl.addEventListener('change', () => {
    clampUnit(targetRtpEl);
    updateSums();
  });
  const primarySumEl = root.querySelector<HTMLElement>('#primary-sum')!;
  const specialSumEl = root.querySelector<HTMLElement>('#special-sum')!;
  const rtpReadout = root.querySelector<HTMLElement>('#weights-rtp')!;
  const saveBtn = root.querySelector<HTMLButtonElement>('#weights-save')!;

  // Mirrors greedykingWeightsSchema: primary and special are each a distribution
  // over [0,1] weights summing to 1.0 (±0.01). Gate Propose on both tiers.
  function tierValidity(inputs: Map<string, HTMLInputElement>, label: string): { ok: boolean; reason: string } {
    const vals = [...inputs.values()].map((i) => Number(i.value) || 0);
    if (vals.some((v) => v < 0 || v > 1)) {
      return { ok: false, reason: `${label}: each weight must be 0–1` };
    }
    if (Math.abs(vals.reduce((a, b) => a + b, 0) - 1) >= 0.01) {
      return { ok: false, reason: `${label} weights must sum to 1.0 (±0.01)` };
    }
    return { ok: true, reason: '' };
  }
  function formValidity(): { ok: boolean; reason: string } {
    const p = tierValidity(primaryInputs, 'primary');
    return p.ok ? tierValidity(specialInputs, 'special') : p;
  }

  function sumOf(inputs: Map<string, HTMLInputElement>): number {
    let sum = 0;
    for (const input of inputs.values()) sum += Number(input.value) || 0;
    return sum;
  }

  function setSumLabel(el: HTMLElement, sum: number): void {
    el.textContent = `sum: ${sum.toFixed(4)}`;
    el.style.color =
      Math.abs(sum - 1) < 0.005 ? 'var(--success)' : Math.abs(sum - 1) < 0.05 ? 'var(--warn)' : 'var(--error)';
  }

  function updateSums(): void {
    setSumLabel(primarySumEl, sumOf(primaryInputs));
    setSumLabel(specialSumEl, sumOf(specialInputs));
    rtpReadout.textContent = `target RTP: ${Number(targetRtpEl.value).toFixed(3)}`;
    const v = formValidity();
    saveBtn.disabled = !v.ok;
    saveBtn.title = v.ok ? '' : v.reason;
  }
  targetRtpEl.addEventListener('input', updateSums);

  function fillForm(value: WeightsOverride): void {
    for (const [s, input] of primaryInputs) input.value = String(value.primary[s] ?? 0);
    for (const [s, input] of specialInputs) input.value = String(value.special[s] ?? 0);
    targetRtpEl.value = String(value.targetRtp ?? 0.98);
    updateSums();
  }

  function readForm(): WeightsOverride {
    const primary: Record<string, number> = {};
    for (const [s, input] of primaryInputs) primary[s] = Number(input.value) || 0;
    const special: Record<string, number> = {};
    for (const [s, input] of specialInputs) special[s] = Number(input.value) || 0;
    return {
      version: 3,
      primary,
      special,
      targetRtp: Number(targetRtpEl.value) || undefined,
    };
  }

  // Load current effective. Falls back to module defaults if the integrator
  // override predates the two-tier schema bump.
  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'weights');
  const stored = current as Partial<WeightsOverride> | null;
  if (stored && stored.primary && stored.special) {
    fillForm(stored as WeightsOverride);
  } else {
    fillForm(DEFAULT_WEIGHTS);
  }

  const msgEl = root.querySelector<HTMLElement>('#weights-msg')!;

  root.querySelector<HTMLButtonElement>('#weights-normalize')!.addEventListener('click', () => {
    for (const inputs of [primaryInputs, specialInputs]) {
      const sum = sumOf(inputs);
      if (sum <= 0) continue;
      for (const input of inputs.values()) {
        input.value = ((Number(input.value) || 0) / sum).toFixed(5);
      }
    }
    updateSums();
  });

  root.querySelector<HTMLButtonElement>('#weights-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_WEIGHTS);
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  // Special-tier preset chips — fill only the `special` map and leave
  // primary + targetRtp untouched. Operator still has to hit Propose
  // change to push it through the two-admin flow.
  root.querySelectorAll<HTMLButtonElement>('#special-presets button[data-preset]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const presetId = btn.dataset.preset;
      const preset = SPECIAL_PRESETS.find((p) => p.id === presetId);
      if (!preset) return;
      for (const [s, input] of specialInputs) {
        input.value = String(preset.special[s] ?? 0);
      }
      updateSums();
      msgEl.textContent = `Special preset “${preset.label}” applied (not saved).`;
      msgEl.className = 'muted';
    });
  });

  root.querySelector<HTMLButtonElement>('#weights-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = weightsSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'weights',
        parsed.data as WeightsOverride,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  // Simulate
  const simResult = root.querySelector<HTMLElement>('#sim-result')!;
  const simMsg = root.querySelector<HTMLElement>('#sim-msg')!;
  root.querySelector<HTMLButtonElement>('#sim-run')!.addEventListener('click', async () => {
    simMsg.textContent = 'Running…';
    simMsg.className = 'muted';
    const rounds = Number(root.querySelector<HTMLInputElement>('#sim-rounds')!.value) || 10_000;
    const bet = root.querySelector<HTMLInputElement>('#sim-bet')!.value || '1000';
    try {
      const sim = await deps.client.simulateWeights(deps.integratorId, {
        rounds,
        betPerSlot: bet,
        weights: readForm(),
      });
      simResult.innerHTML = renderSimResult(sim);
      simMsg.textContent = '';
    } catch (err) {
      simMsg.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      simMsg.className = 'error';
    }
  });

  // Verify
  const verifyResult = root.querySelector<HTMLElement>('#verify-result')!;
  root.querySelector<HTMLButtonElement>('#verify-run')!.addEventListener('click', async () => {
    const id = root.querySelector<HTMLInputElement>('#verify-id')!.value.trim();
    if (!id) return;
    verifyResult.innerHTML = '<p class="muted">Verifying…</p>';
    try {
      const v = await deps.client.verifyRound(deps.roundEngineBaseUrl, id);
      verifyResult.innerHTML = renderVerifyResult(v);
    } catch (err) {
      verifyResult.innerHTML = `<p class="error">${
        err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err)
      }</p>`;
    }
  });

  // Pending
  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'weights' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#weights-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — weights now live.';
          msgEl.className = 'muted';
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'weights');
          const value = fresh.value as Partial<WeightsOverride> | null;
          if (value && value.primary && value.special) {
            fillForm(value as WeightsOverride);
          } else {
            fillForm(DEFAULT_WEIGHTS);
          }
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
  }
  await refreshPending();
}

function renderSimResult(s: SimulateResponse): string {
  const pct = (n: number) => `${(n * 100).toFixed(2)}%`;
  const primarySlots = Object.keys(s.primaryCounts);
  const specialSlots = Object.keys(s.specialCounts);
  return `
    <div class="kv" style="margin-top:12px">
      <dt>Rounds</dt><dd>${s.rounds.toLocaleString()}</dd>
      <dt>Empirical RTP (incl. specials)</dt><dd style="color:var(--accent)">${pct(s.empiricalRtp)}</dd>
      <dt>Theoretical RTP (primary tier)</dt><dd>${pct(s.theoreticalRtp)}</dd>
      <dt>Worst single-slot RTP</dt><dd style="color:${s.worstFoodRtp >= 1 ? 'var(--error)' : 'var(--success)'}">${pct(s.worstFoodRtp)}</dd>
      <dt>Specials fired</dt><dd>${s.jackpotCount} (pizza ${pct(s.pizzaFreq)} · salad ${pct(s.saladFreq)})</dd>
    </div>
    <h3 style="margin-top:16px">Primary tier — empirical vs configured</h3>
    <table>
      <thead><tr><th>Slot</th><th>Configured</th><th>Empirical hit-rate</th><th>Empirical RTP</th></tr></thead>
      <tbody>${primarySlots
        .map(
          (k) => `<tr>
            <td><code>${k}</code></td>
            <td>${pct(s.theoreticalPerItemHitRate[k] ?? 0)}</td>
            <td>${pct((s.primaryCounts[k] ?? 0) / s.rounds)}</td>
            <td>${pct(s.perItemRtp[k] ?? 0)}</td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>
    <h3 style="margin-top:16px">Special tier — empirical frequency</h3>
    <table>
      <thead><tr><th>Outcome</th><th>Empirical rate</th></tr></thead>
      <tbody>${specialSlots
        .map(
          (k) => `<tr>
            <td><code>${k}</code></td>
            <td>${pct((s.specialCounts[k] ?? 0) / s.rounds)}</td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>
  `;
}

function renderVerifyResult(v: VerifyResponse): string {
  // Tri-state verdict (see rounds-explorer.ts).
  const verdict: string = (v as { verdict?: string }).verdict ?? (v.valid ? 'VALID' : 'INVALID');
  const verdictColor =
    verdict === 'VALID'
      ? 'var(--success)'
      : verdict === 'UNVERIFIABLE'
        ? 'var(--warn, #fde047)'
        : 'var(--error)';
  const expected = v.expectedResult as { primary?: string; special?: string | null } | null;
  const expectedStr = expected
    ? `${expected.primary ?? '?'}${expected.special ? ` + ${expected.special}` : ''}`
    : String(v.expectedResult ?? '?');
  const derived = `${v.derivedResult.primary}${v.derivedResult.special ? ` + ${v.derivedResult.special}` : ''}`;
  return `
    <div class="kv" style="margin-top:12px">
      <dt>Verdict</dt><dd style="color:${verdictColor};font-weight:600">${verdict}</dd>
      <dt>Hash match</dt><dd>${v.hashMatch}</dd>
      <dt>Result match</dt><dd>${v.resultMatch}</dd>
      <dt>Expected</dt><dd><code>${expectedStr}</code></dd>
      <dt>Derived</dt><dd><code>${derived}</code></dd>
      <dt>Primary roll</dt><dd>${v.derivedPrimaryRoll.toFixed(6)}</dd>
      <dt>Special roll</dt><dd>${v.derivedSpecialRoll.toFixed(6)}</dd>
      <dt>Server seed</dt><dd><code>${v.serverSeed.slice(0, 16)}…</code></dd>
      <dt>Server seed hash</dt><dd><code>${v.serverSeedHash.slice(0, 16)}…</code></dd>
      <dt>Client seed</dt><dd><code>${v.clientSeed}</code></dd>
      <dt>Nonce</dt><dd>${v.nonce}</dd>
      <dt>Weight table</dt><dd>v${v.weightTable.version} · ${Object.keys(v.weightTable.primary).length} primary + ${Object.keys(v.weightTable.special).length} special</dd>
    </div>
  `;
}
